import { useMemo, useState, useSyncExternalStore } from 'react';
import { recorder } from '../../app/recorder';
import { PHASE_OPTIONS } from '../../ui/halftime/phaseOptions';
import {
  isCrossFormatCarry,
  normalizeHypothesisText,
  preSelectableEntries,
  shapesFor,
} from '../../domain/setup';
import type { CarryCandidate, PreMatchCandidate } from '../../domain/setup';
import { MATCH_FORMATS, MATCH_PURPOSES } from '../../types';
import type { MatchFormat, MatchPurpose } from '../../types';
import { matchPurposeLabel } from '../../domain/homeB';
import { Icon } from '../Icon';
import { PhaseIcon } from '../PhaseIcon';
import { phaseLabelB } from '../phases';
import { PreflightChipB } from '../PreflightChipB';
import { AddPatternSheet } from './AddPatternSheet';
import { NewOpponentSheet } from './NewOpponentSheet';
import { useSetup } from './useSetup';

/**
 * B spec §2.4 — **match setup** (build unit B-7): one sectioned screen in the spec's order —
 * opponent / competition / date, format, purpose, prep context (carry-over + the scenario-34
 * emphasis card), the pre-selected library, the microphone card, and the exits.
 *
 * ── The two required gates land here (F-2, ruled) ────────────────────────────
 *
 * Format (PRE-8) and purpose (F-2) are required choices with **no default**: no chip and no
 * card is pressed until the user presses one, and *Begin match day* is inert until both are
 * chosen, with the reason rendered beside it (the §6.0a clamp pattern — visible before the
 * user reaches for the control, not raised after). The gate is B's UI by Andrew's explicit
 * ruling; the engine's `ERR_MATCH_FORMAT_REQUIRED` stays the backstop and `useSetup.begin`
 * re-checks on the tap, so the gate is a behavior, not a disabled attribute.
 *
 * ── Keyboard reachability is structural (the B-6 pattern) ────────────────────
 *
 * The primary action lives in a sticky header at the TOP of the screen's own scroll container,
 * exactly as `SheetB` established: the iOS layout viewport never resizes for the keyboard and
 * `interactive-widget` never shipped, so a top action cannot be covered while the competition
 * field has focus. Free-text entry beyond the two inline fields happens in `SheetB` sheets
 * (new opponent, custom pattern), which carry the same rule.
 *
 * ── One selection, two offer surfaces ────────────────────────────────────────
 *
 * The selection is `useSetup`'s candidate list. The carry-over block offers PRE-5's candidates;
 * the library offers PRE-9's slot-free seed entries — and a seed whose normalized text matches
 * an offered carry candidate is NOT rendered in the library, because PRE-9's rule is *"exactly
 * one candidate exists and it is the carried one"*: the carry row is that key's one owner, so
 * no key ever has two checkboxes. `normalizeHypothesisText` — PRE-5's own normalizer — decides
 * membership here for the same reason it decides the merge.
 */

/** The recorder's mic state, subscribed the `useSyncExternalStore` way (module-stable fns). */
const subscribeMic = (listener: () => void): (() => void) => recorder.subscribe(listener);
const readMic = (): string => recorder.micState();

export interface SetupScreenProps {
  /** Back — the overlay closes; an unstarted setup stays and resumes (§2.2). */
  readonly onClose: () => void;
  /** PRE-7's exit: `StartCapture` committed; the shell swaps to the takeover (B-8's seam). */
  readonly onStarted: (matchId: string) => void;
}

export function SetupScreen({ onClose, onStarted }: SetupScreenProps) {
  const { state, setup, actions } = useSetup(onStarted);
  const [opponentSheet, setOpponentSheet] = useState(false);
  const [patternSheet, setPatternSheet] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);
  const [armedDiscard, setArmedDiscard] = useState(false);
  const mic = useSyncExternalStore(subscribeMic, readMic);

  const selectedKeys = useMemo(
    () => new Set(state.selected.map((candidate) => normalizeHypothesisText(candidate.text))),
    [state.selected],
  );
  const carryKeys = useMemo(
    () => new Set((state.teamContext?.carryForward ?? []).map((carry) => carry.key)),
    [state.teamContext],
  );
  /**
   * PRE-9's offer, grouped by phase in `PHASE_OPTIONS` order — `preSelectableEntries` is the
   * one selector (the screen never re-derives "builtin, slot-free"); carry-owned keys excluded
   * per the file header's exactly-once rule.
   */
  const seedsByPhase = useMemo(() => {
    if (state.context === null) return [];
    const seeds = preSelectableEntries(state.context.templates).map((seed) => ({
      ...seed,
      key: normalizeHypothesisText(seed.entry.text),
    }));
    return PHASE_OPTIONS.map((option) => ({
      option,
      seeds: seeds.filter((seed) => seed.entry.phase === option.phase && !carryKeys.has(seed.key)),
    }));
  }, [carryKeys, state.context]);

  if (state.phase === 'loading')
    return <p className="homeb-empty">Getting the match ready…</p>;
  if (state.phase === 'failed')
    return (
      <p className="homeb-empty" role="alert">
        The setup screen could not be loaded. The log has the detail.
      </p>
    );

  const context = state.context;
  if (context === null) return null;

  const canBegin = setup !== undefined && setup.format !== null && setup.purpose !== null;
  const gateNote =
    setup === undefined
      ? 'Pick an opponent to start a match.'
      : setup.format === null && setup.purpose === null
        ? 'Format and purpose still need choosing — neither has a default.'
        : setup.format === null
          ? 'Format still needs choosing — there is no default.'
          : setup.purpose === null
            ? 'Purpose still needs choosing — there is no default.'
            : null;

  const customFor = (phase: string): PreMatchCandidate[] =>
    state.selected.filter(
      (candidate) =>
        candidate.sourceTemplateId === null &&
        candidate.carriedFromMatchId === null &&
        candidate.phase === phase,
    );

  const carrySelectedId = (carry: CarryCandidate): string | undefined =>
    state.selected.find((candidate) => normalizeHypothesisText(candidate.text) === carry.key)?.id;

  const prepEntries = state.prep?.card.adjustments.filter((entry) => entry.status === 'accepted') ?? [];

  return (
    <div
      className="setup-b"
      data-testid="setup-b"
      data-read={String(state.reads)}
      data-selected={String(state.selected.length)}
      data-matchid={setup?.id ?? ''}
      data-canbegin={canBegin ? 'true' : 'false'}
    >
      {/* Sticky top header — the SheetB keyboard rule, on a screen (see the file header). */}
      <header className="setup-b__top">
        <div className="setup-b__topRow">
          <button type="button" className="setup-b__back" data-testid="setup-back" onClick={onClose}>
            Back
          </button>
          <h1 className="setup-b__title">Match setup</h1>
        </div>
        <button
          type="button"
          className="setup-b__begin"
          data-testid="setup-begin"
          disabled={!canBegin || state.busy}
          onClick={() => {
            void actions.begin();
          }}
        >
          Begin match day{state.selected.length > 0 ? ` (${String(state.selected.length)})` : ''}
        </button>
        {gateNote !== null && (
          <p className="setup-b__gate" data-testid="setup-gate-note">
            {gateNote}
          </p>
        )}
      </header>

      {state.notice !== null && (
        <p className="setup-b__notice" data-testid="setup-notice" role="status">
          {state.notice}
          <button
            type="button"
            className="setup-b__dismiss"
            onClick={actions.dismissNotice}
          >
            Dismiss
          </button>
        </p>
      )}

      {/* ── 1. Opponent, competition, date ─────────────────────────────────── */}
      <section className="setup-section">
        <h2 className="setup-section__title">Opponent</h2>
        <div className="setup-opponent">
          <select
            className="setup-b__select"
            data-testid="setup-opponent-select"
            aria-label="Opponent"
            value={setup?.teamId ?? ''}
            onChange={(event) => {
              if (event.target.value !== '') void actions.pickOpponent(event.target.value);
            }}
          >
            <option value="" disabled>
              Choose an opponent…
            </option>
            {context.teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="setup-b__minor"
            data-testid="setup-new-opponent"
            onClick={() => {
              setOpponentSheet(true);
            }}
          >
            New opponent
          </button>
        </div>

        {setup !== undefined && (
          <div className="setup-details">
            <label className="setup-b__label" htmlFor="setup-comp">
              Competition (optional)
            </label>
            <input
              id="setup-comp"
              key={`comp-${setup.id}`}
              className="setup-b__field"
              data-testid="setup-comp"
              type="text"
              autoComplete="off"
              placeholder="League, cup, friendly…"
              defaultValue={setup.comp ?? ''}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value !== (setup.comp ?? ''))
                  void actions.editSetup({ comp: value === '' ? null : value });
              }}
            />
            <label className="setup-b__label" htmlFor="setup-date">
              Date
            </label>
            <input
              id="setup-date"
              className="setup-b__field"
              data-testid="setup-date"
              type="date"
              value={setup.date}
              onChange={(event) => {
                if (event.target.value !== '') void actions.editSetup({ date: event.target.value });
              }}
            />
          </div>
        )}
      </section>

      {setup !== undefined && (
        <>
          {/* ── 2. Format — required, no default (PRE-8) ─────────────────────── */}
          <section className="setup-section">
            <h2 className="setup-section__title">Format</h2>
            <div className="setup-chips" role="radiogroup" aria-label="Format">
              {MATCH_FORMATS.map((format: MatchFormat) => (
                <button
                  key={format}
                  type="button"
                  role="radio"
                  aria-checked={setup.format === format}
                  className="setup-chip"
                  data-testid={`setup-format-${format}`}
                  onClick={() => {
                    void actions.editSetup({ format });
                  }}
                >
                  {format}
                </button>
              ))}
            </div>

            {setup.format !== null && (
              <>
                <div className="setup-halflength" data-testid="setup-halflength">
                  <span className="setup-b__label">Half length</span>
                  <div className="setup-halflength__row">
                    <button
                      type="button"
                      className="setup-halflength__step"
                      data-testid="setup-halflength-minus"
                      aria-label="Shorter halves"
                      disabled={state.busy}
                      onClick={() => {
                        void actions.stepHalfLength(-1);
                      }}
                    >
                      −
                    </button>
                    <span className="setup-halflength__value" data-testid="setup-halflength-value">
                      {setup.halfLengthMin} min
                    </span>
                    <button
                      type="button"
                      className="setup-halflength__step"
                      data-testid="setup-halflength-plus"
                      aria-label="Longer halves"
                      disabled={state.busy}
                      onClick={() => {
                        void actions.stepHalfLength(1);
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <label className="setup-b__label" htmlFor="setup-formation">
                  Formation guess (optional) — outfield players only; the keeper is assumed
                </label>
                <select
                  id="setup-formation"
                  className="setup-b__select"
                  data-testid="setup-formation"
                  value={setup.formationGuess ?? ''}
                  onChange={(event) => {
                    void actions.editSetup({
                      formationGuess: event.target.value === '' ? null : event.target.value,
                    });
                  }}
                >
                  <option value="">No guess</option>
                  {shapesFor(setup.format).map((shape) => (
                    <option key={shape} value={shape}>
                      {shape}
                    </option>
                  ))}
                </select>
              </>
            )}
          </section>

          {/* ── 3. Purpose — required, no default (F-2) ──────────────────────── */}
          <section className="setup-section">
            <h2 className="setup-section__title">Purpose</h2>
            <div className="setup-purpose" role="radiogroup" aria-label="Purpose">
              {MATCH_PURPOSES.map((purpose: MatchPurpose) => (
                <button
                  key={purpose}
                  type="button"
                  role="radio"
                  aria-checked={setup.purpose === purpose}
                  className="setup-purpose__card"
                  data-testid={`setup-purpose-${purpose}`}
                  onClick={() => {
                    void actions.editSetup({ purpose });
                  }}
                >
                  <span className="setup-purpose__name">{matchPurposeLabel(purpose)}</span>
                  <span className="setup-purpose__what">
                    {purpose === 'teamMatch'
                      ? 'We are playing them. Halftime offers the adjustments workflow.'
                      : 'Watching a future opponent. Points of emphasis come after full time.'}
                  </span>
                </button>
              ))}
            </div>
            {/* §2.6 — *"the Test connection chip … surfaced on match setup when purpose = team
                match"*. This is the last moment before kickoff when a broken configuration can
                still be fixed: a team match's halftime workflow will want Coach Claude in about
                an hour, and finding out then is finding out too late. An advance scout does not
                need it until full time, so the chip is not offered here for one (§2.6). Added by
                B-19, the unit that owns the chip — before it, no chip existed to surface. */}
            {setup.purpose === 'teamMatch' && (
              <div className="setup-preflight" data-testid="setup-preflight">
                {/* B-24 — `surface="setup"`, because there is no credential control on this
                    screen: the chip's copy must send the reader to the More tab rather than to a
                    field "above" that is not here. For the same reason the note says "fix it now"
                    and no longer "fix it here" — here is where you FIND OUT, not where you fix. */}
                <PreflightChipB
                  surface="setup"
                  note="Halftime's adjustments workflow needs Coach Claude. Checking now costs nothing and takes a second — a credential that is missing or wrong is far easier to fix now than at the break."
                />
              </div>
            )}
          </section>

          {/* ── 4. Prep context — carry-over + scenario 34's moment of use ───── */}
          {state.prep !== null && (
            <section className="setup-section" data-testid="setup-prep">
              <h2 className="setup-section__title">Points of emphasis</h2>
              <div className="setup-prep">
                <button
                  type="button"
                  className="setup-prep__head"
                  data-testid="setup-prep-toggle"
                  aria-expanded={prepOpen}
                  onClick={() => {
                    setPrepOpen((open) => !open);
                  }}
                >
                  <span>
                    From scouting them on {state.prep.matchDate} · {prepEntries.length}{' '}
                    {prepEntries.length === 1 ? 'point' : 'points'}
                  </span>
                  <span className="setup-prep__chev">{prepOpen ? 'Hide' : 'Show'}</span>
                </button>
                {prepOpen && (
                  <ol className="setup-prep__entries" data-testid="setup-prep-entries">
                    {prepEntries.map((entry) => (
                      <li key={entry.entryId} className="setup-prep__entry">
                        <span className="setup-prep__phase">
                          <PhaseIcon phase={entry.phase} />
                          {phaseLabelB(entry.phase)}
                        </span>
                        <span className="setup-prep__text">{entry.text}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>
          )}

          {state.teamContext !== null && state.teamContext.carryForward.length > 0 && (
            <section className="setup-section" data-testid="setup-carry">
              <h2 className="setup-section__title">Carry over from past matches</h2>
              <ul className="setup-list">
                {state.teamContext.carryForward.map((carry) => {
                  const selectedId = carrySelectedId(carry);
                  return (
                    <li key={carry.key} className="setup-row" data-carry-key={carry.key}>
                      <label className="setup-row__label">
                        <input
                          type="checkbox"
                          className="setup-check"
                          checked={selectedId !== undefined}
                          disabled={state.busy}
                          onChange={() => {
                            if (selectedId !== undefined) actions.deselectKey(carry.key);
                            else actions.selectCarry(carry);
                          }}
                        />
                        <span className="setup-row__body">
                          <span className="setup-row__text">{carry.text}</span>
                          <span className="setup-row__meta">
                            <span
                              className="setup-badge"
                              data-status={carry.carriedStatus}
                            >
                              {carry.carriedStatus === 'confirmed'
                                ? `Confirmed ×${String(carry.carriedCount)}`
                                : 'Ruled out before'}
                            </span>
                            <span>
                              in {String(carry.carriedStatus === 'confirmed' ? carry.confirmedInMatches : carry.invalidatedInMatches)}{' '}
                              of {String(carry.priorMatchCount)}{' '}
                              {carry.priorMatchCount === 1 ? 'match' : 'matches'}
                            </span>
                            {isCrossFormatCarry(setup.format, carry) && (
                              <span className="setup-badge" data-status="crossFormat">
                                seen in {carry.carriedFormat ?? ''}
                              </span>
                            )}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* ── 5. The library — ~49 pre-selected, grouped by phase (PRE-9) ──── */}
          <section className="setup-section" data-testid="setup-library">
            <div className="setup-library__head">
              <h2 className="setup-section__title">Pattern library</h2>
              <button
                type="button"
                className="setup-b__minor"
                data-testid="setup-add-pattern"
                onClick={() => {
                  setPatternSheet(true);
                }}
              >
                Add your own
              </button>
            </div>
            <p className="setup-section__note">
              The whole library starts selected — untick what you will not watch for.
            </p>
            {seedsByPhase.map(({ option, seeds }) => {
              const customs = customFor(option.phase);
              if (seeds.length === 0 && customs.length === 0) return null;
              const checkedCount =
                seeds.filter((seed) => selectedKeys.has(seed.key)).length + customs.length;
              return (
                <div
                  key={option.phase}
                  className="setup-group"
                  data-testid={`setup-group-${option.phase}`}
                >
                  <h3 className="setup-group__title">
                    <PhaseIcon phase={option.phase} />
                    <span>{option.label}</span>
                    <span className="setup-group__count">
                      {String(checkedCount)}/{String(seeds.length + customs.length)}
                    </span>
                  </h3>
                  <ul className="setup-list">
                    {seeds.map((seed) => (
                      <li key={seed.key} className="setup-row" data-key={seed.key}>
                        <label className="setup-row__label">
                          <input
                            type="checkbox"
                            className="setup-check"
                            checked={selectedKeys.has(seed.key)}
                            onChange={() => {
                              if (selectedKeys.has(seed.key)) actions.deselectKey(seed.key);
                              else actions.selectSeed(seed.templateId, seed.entry);
                            }}
                          />
                          <span className="setup-row__text">{seed.entry.text}</span>
                        </label>
                      </li>
                    ))}
                    {customs.map((candidate) => (
                      <li key={candidate.id} className="setup-row" data-custom="true">
                        <span className="setup-row__label">
                          <span className="setup-row__body">
                            <span className="setup-row__text">{candidate.text}</span>
                            <span className="setup-row__meta">
                              <span className="setup-badge" data-status="yours">
                                Yours
                              </span>
                            </span>
                          </span>
                        </span>
                        <button
                          type="button"
                          className="setup-b__minor"
                          data-testid={`setup-remove-${candidate.id}`}
                          onClick={() => {
                            actions.removeCandidate(candidate.id);
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>

          {/* ── 6. Microphone (FLAG-4: declining is fine; flags file silently) ── */}
          <section className="setup-section" data-testid="setup-mic" data-mic={mic}>
            <h2 className="setup-section__title">
              <Icon name="voice-note" /> Voice notes
            </h2>
            {mic === 'granted' ? (
              <p className="setup-section__note">Microphone ready — flags can carry voice notes.</p>
            ) : mic === 'denied' ? (
              <p className="setup-section__note">
                The microphone is blocked for this app in iOS Settings. Flags will file silently.
              </p>
            ) : mic === 'unavailable' ? (
              <p className="setup-section__note">
                No microphone is available here. Flags will file silently.
              </p>
            ) : (
              <>
                <p className="setup-section__note">
                  {mic === 'declined'
                    ? 'Flags will file silently this match. You can still allow the microphone.'
                    : 'Allow the microphone now so a mid-match flag can carry your voice. Declining is fine — flags file silently without it.'}
                </p>
                <div className="setup-mic__actions">
                  <button
                    type="button"
                    className="setup-b__minor"
                    data-testid="setup-mic-allow"
                    onClick={() => {
                      // Must be called synchronously from the tap (iOS user-activation rule).
                      void recorder.requestMicrophone();
                    }}
                  >
                    Allow the microphone
                  </button>
                  {mic !== 'declined' && (
                    <button
                      type="button"
                      className="setup-b__minor"
                      data-testid="setup-mic-later"
                      onClick={() => {
                        recorder.declineMicrophone();
                      }}
                    >
                      Not now
                    </button>
                  )}
                </div>
              </>
            )}
          </section>

          {/* ── 7. Discard — available and guarded (§2.2) ────────────────────── */}
          <section className="setup-section setup-discard">
            {!armedDiscard ? (
              <button
                type="button"
                className="setup-b__quiet"
                data-testid="setup-discard"
                disabled={state.busy}
                onClick={() => {
                  setArmedDiscard(true);
                }}
              >
                Discard this setup…
              </button>
            ) : (
              <>
                <p className="setup-section__note">
                  This deletes the setup and everything selected on it. Nothing has been captured
                  yet.
                </p>
                <button
                  type="button"
                  className="setup-b__destructive"
                  data-testid="setup-discard-confirm"
                  disabled={state.busy}
                  onClick={() => {
                    void actions.discard(onClose);
                  }}
                >
                  Discard for good
                </button>
                <button
                  type="button"
                  className="setup-b__quiet"
                  data-testid="setup-discard-keep"
                  onClick={() => {
                    setArmedDiscard(false);
                  }}
                >
                  Keep setting up
                </button>
              </>
            )}
          </section>
        </>
      )}

      {opponentSheet && (
        <NewOpponentSheet
          busy={state.busy}
          onCreate={(name, club, ageGroup) => {
            void actions.createOpponent(name, club, ageGroup).then(() => {
              setOpponentSheet(false);
            });
          }}
          onClose={() => {
            setOpponentSheet(false);
          }}
        />
      )}

      {patternSheet && (
        <AddPatternSheet
          onAdd={(text, phase) => {
            actions.addCustom(text, phase);
            setPatternSheet(false);
          }}
          onClose={() => {
            setPatternSheet(false);
          }}
        />
      )}
    </div>
  );
}
